import type { Breakpoint } from "../../hooks/useBreakpoint";
import {
  FALLBACK_SCROLL_PAGES,
  STATIC_CAMERA_POS,
  SERVICES_CAMERA,
  SERVICES_END_CAMERA,
  DECKS_CAMERA,
} from "./constants";
import {
  deriveHomeThresholds,
  DEFAULT_HOME_MEASUREMENT,
  type SceneThresholds,
} from "./sceneThresholds";
import { pagesFromMeasurement, type ScrollMeasurement } from "./scrollLayout";

export type { SceneThresholds };

type Vec3 = [number, number, number];
type Camera = { pos: Vec3; target: Vec3 };
type TierMap<T> = Record<Breakpoint, T>;

const sameForAllTiers = <T,>(desktop: T): TierMap<T> => ({
  desktop,
  tablet: desktop,
  mobile: desktop,
});

// Pre-measurement fallback only; the measured overlay height replaces it on
// the first ScrollLayoutMeasurer tick (the old hand-tuned mobile pages are
// gone — the taller mobile column is simply measured).
export const FALLBACK_PAGES_BY_TIER: TierMap<number> = {
  desktop: FALLBACK_SCROLL_PAGES,
  tablet: FALLBACK_SCROLL_PAGES,
  mobile: 21.5,
};

export const SCROLL_DAMPING_BY_TIER: TierMap<number> = sameForAllTiers(0.15);
export const STATIC_CAMERA_POS_BY_TIER: TierMap<Vec3> = {
  desktop: STATIC_CAMERA_POS,
  tablet: STATIC_CAMERA_POS,
  // Portrait crops the wide arch — pull the camera back along its view ray
  // so it fits the frame width. Was 92.5 (+25%); brought back in to 85.6
  // (+16%) so the arch fills more of the tall portrait frame (less empty
  // sky) while the columns still clear the edges on 360px-wide phones.
  mobile: [85.6, -18.5, 0.144],
};
// The staircase model ends at a landing that the pulled-back mobile camera
// exposes above the viewport bottom, so the stairs read as floating. Lower the
// whole gate+stairs+fog group on mobile until that landing (and the ground-fog
// card hugging it) sits below the bottom edge — the stairs then always start
// at the bottom of the screen.
export const HOME_GATE_GROUP_OFFSET_BY_TIER: TierMap<Vec3> = {
  desktop: [0, 0, 0],
  tablet: [0, 0, 0],
  mobile: [0, -6, 0],
};

export const SERVICES_CAMERA_BY_TIER: TierMap<Camera> = sameForAllTiers(SERVICES_CAMERA);
export const SERVICES_END_CAMERA_BY_TIER: TierMap<Camera> =
  sameForAllTiers(SERVICES_END_CAMERA);
// Same camera on every tier. (A mobile pullback along the view ray was tried
// and reverted: any pulled-back position sits deeper in the decks cloud field,
// hazing the whole frame on real devices. Portrait fitting is done by
// shrinking + gathering the boxes instead — see the two maps below.)
export const DECKS_CAMERA_BY_TIER: TierMap<Camera> = sameForAllTiers(DECKS_CAMERA);

// Multiplier on the deck-box visibility scale (DecksPart). Shrinks the boxes
// around their own centers so both fit the portrait frame fully.
export const DECKS_BOX_SCALE_BY_TIER: TierMap<number> = {
  desktop: 1,
  tablet: 1,
  mobile: 0.48,
};

// World units each deck box moves inward along the decks camera's screen-x
// axis (0 = authored spread). Shrinking alone leaves the portrait gap between
// the boxes too wide — sliding them together perpendicular to the view closes
// the gap without changing their depth (and so their on-screen size).
export const DECKS_BOX_GATHER_BY_TIER: TierMap<number> = {
  desktop: 0,
  tablet: 0,
  mobile: 55,
};

// World units the Guidance box (screen-right, renders higher) moves down and
// the Heavens Bridge box (screen-left, renders lower) moves up along the
// decks camera's screen-y axis (depth unchanged). The authored composition
// staggers the pair; the narrow portrait layout reads better with both boxes
// on one visual level.
export const DECKS_BOX_LEVEL_BY_TIER: TierMap<number> = {
  desktop: 0,
  tablet: 0,
  mobile: 6,
};

// Extra scale on the Guidance Edition box only (the screen-right one). Its
// pose renders slightly shorter on screen than the Heavens Bridge box; with
// the pair leveled, the mismatched heights are what still reads as "not
// aligned" on the narrow mobile layout.
export const DECKS_BOX_BALANCE_BY_TIER: TierMap<number> = {
  desktop: 1,
  tablet: 1,
  mobile: 1.05,
};

// --- Accessors -----------------------------------------------------------
export const pagesFor = (bp: Breakpoint): number => FALLBACK_PAGES_BY_TIER[bp];
export const dampingFor = (bp: Breakpoint): number => SCROLL_DAMPING_BY_TIER[bp];
export const staticCameraFor = (bp: Breakpoint): Vec3 => STATIC_CAMERA_POS_BY_TIER[bp];
export const servicesCameraFor = (bp: Breakpoint): Camera => SERVICES_CAMERA_BY_TIER[bp];
export const servicesEndCameraFor = (bp: Breakpoint): Camera =>
  SERVICES_END_CAMERA_BY_TIER[bp];
export const decksCameraFor = (bp: Breakpoint): Camera => DECKS_CAMERA_BY_TIER[bp];
export const decksBoxScaleFor = (bp: Breakpoint): number => DECKS_BOX_SCALE_BY_TIER[bp];
export const decksBoxGatherFor = (bp: Breakpoint): number => DECKS_BOX_GATHER_BY_TIER[bp];
export const decksBoxLevelFor = (bp: Breakpoint): number => DECKS_BOX_LEVEL_BY_TIER[bp];
export const decksBoxBalanceFor = (bp: Breakpoint): number => DECKS_BOX_BALANCE_BY_TIER[bp];

export type SceneScalars = {
  pages: number;
  tScale: number;
  tMax: number;
  damping: number;
  thresholds: SceneThresholds;
  staticCamera: Vec3;
  servicesCamera: Camera;
  servicesEndCamera: Camera;
  decksCamera: Camera;
  decksBoxScale: number;
  decksBoxGather: number;
  decksBoxLevel: number;
  decksBoxBalance: number;
  homeGateGroupOffset: Vec3;
};

/** Resolve every scene scalar for one breakpoint. t-space is "screens
 *  scrolled" (t = offset × (pages − 1)). With no measurement yet, falls back
 *  to DEFAULT_HOME_MEASUREMENT-derived values so the first frames behave. */
export function sceneScalarsFor(
  bp: Breakpoint,
  measurement: ScrollMeasurement | null = null,
): SceneScalars {
  const pages = pagesFromMeasurement(measurement, FALLBACK_PAGES_BY_TIER[bp]);
  const derived =
    (measurement && deriveHomeThresholds(measurement)) ??
    deriveHomeThresholds(DEFAULT_HOME_MEASUREMENT)!;
  return {
    pages,
    tScale: Math.max(1, pages - 1),
    tMax: derived.tMax,
    damping: SCROLL_DAMPING_BY_TIER[bp],
    thresholds: derived.thresholds,
    staticCamera: STATIC_CAMERA_POS_BY_TIER[bp],
    servicesCamera: SERVICES_CAMERA_BY_TIER[bp],
    servicesEndCamera: SERVICES_END_CAMERA_BY_TIER[bp],
    decksCamera: DECKS_CAMERA_BY_TIER[bp],
    decksBoxScale: DECKS_BOX_SCALE_BY_TIER[bp],
    decksBoxGather: DECKS_BOX_GATHER_BY_TIER[bp],
    decksBoxLevel: DECKS_BOX_LEVEL_BY_TIER[bp],
    decksBoxBalance: DECKS_BOX_BALANCE_BY_TIER[bp],
    homeGateGroupOffset: HOME_GATE_GROUP_OFFSET_BY_TIER[bp],
  };
}
